import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Heart, Brain, Activity, Users, Sparkles, ArrowRight, Flame, Lightbulb, Trophy, Zap, LogOut, User as UserIcon, TrendingUp, ShoppingBag, Menu, X, Newspaper, ChefHat } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import happyWomenImage from "@/assets/happy-women-younger-40s.jpg";
import womanReadingImage from "@/assets/woman-reading-book.jpg";
import womanLearningImage from "@/assets/woman-learning-education.jpg";
import communitySupportImage from "@/assets/women-community-support.jpg";
import womanJournalingImage from "@/assets/woman-journaling-tracking.jpg";
import calmBackgroundImage from "@/assets/calm-background.jpg";
import symptomExplorerImage from "@/assets/symptom-explorer-board.jpg";
import mythBustersImage from "@/assets/myth-busters-split.jpg";
import progressMountainImage from "@/assets/progress-mountain-path.jpg";
import aiInsightsImage from "@/assets/ai-insights-40s.jpg";
import wellnessRecipesImage from "@/assets/wellness-recipes-healthy.jpg";
import womanReadingNewspaperImage from "@/assets/woman-reading-newspaper-gossip.jpg";
import { supabase } from "@/integrations/supabase/client";
import { User } from '@supabase/supabase-js';
import { useToast } from "@/hooks/use-toast";
import { PersonalizedPlanCard } from "@/components/PersonalizedPlanCard";
import { FeatureCard } from "@/components/FeatureCard";
import { FeatureStory } from "@/components/FeatureStory";
import RotatingWelcomeMessage from "@/components/RotatingWelcomeMessage";
import { DailyReflection } from "@/components/DailyReflection";
import { TrialBanner } from "@/components/TrialBanner";
import { SubscriptionStatusCard } from "@/components/SubscriptionStatusCard";
import { Footer } from "@/components/Footer";
import AuraChatButton from "@/components/AuraChatButton";
import SEO from "@/components/SEO";

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [hasCompletedProfile, setHasCompletedProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    /**
     * USER JOURNEY: Initial Landing & Splash Screen Check
     * 
     * When users first visit the app:
     * 1. Check if they've seen the welcome splash → /splash (first time only)
     * 2. Check authentication status
     * 3. If authenticated, verify profile completion
     * 4. Route accordingly:
     *    - No splash seen → /splash
     *    - Authenticated + incomplete profile → /profile-setup
     *    - Authenticated + complete profile → stay on / (home)
     *    - Not authenticated → stay on / (can browse/sign up)
     */
    // If an auth link landed on the root URL, forward to the callback handler
    // so the session token isn't discarded by a splash redirect.
    const hash = window.location.hash;
    const hasAuthToken =
      hash.includes("access_token") ||
      hash.includes("type=signup") ||
      hash.includes("type=recovery") ||
      new URLSearchParams(window.location.search).has("code");
    if (hasAuthToken) {
      // Recovery links must land on the page that asks for a new password.
      const target = hash.includes("type=recovery") ? "/reset-password" : "/auth/callback";
      navigate(target + window.location.search + hash, { replace: true });
      return;
    }

    // Check if user has seen the splash screen
    const hasSeenSplash = localStorage.getItem("aura-splash-seen");
    if (!hasSeenSplash) {
      navigate("/splash");
      return;
    }

    checkAuthStatus();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          // Defer profile check to avoid blocking auth callback
          setTimeout(() => {
            checkProfileStatus(session.user.id);
          }, 0);
        } else {
          setHasCompletedProfile(false);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  const checkAuthStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        await checkProfileStatus(user.id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error checking auth:", error);
      setLoading(false);
    }
  };

  const checkProfileStatus = async (userId: string) => {
    /**
     * Validates user profile completion status.
     * 
     * If profile incomplete → redirect to /profile-setup
     * If profile complete → stay on current page
     */
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("user_id", userId)
        .maybeSingle();

      if (!error && data?.onboarding_completed) {
        setHasCompletedProfile(true);
      } else if (!error && data && !data.onboarding_completed) {
        // Profile exists but onboarding not completed - redirect to profile setup
        setHasCompletedProfile(false);
        navigate("/profile-setup");
      } else if (!data) {
        // No profile exists - redirect to profile setup
        setHasCompletedProfile(false);
        navigate("/profile-setup");
      }
    } catch (error) {
      console.error("Error checking profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    /**
     * "Get Started" button click handler.
     * 
     * Routes users based on their auth and profile status:
     * - Authenticated + profile complete → /tracker (main app)
     * - Not authenticated or incomplete profile → /auth (login/signup)
     */
    if (user && hasCompletedProfile) {
      // User is logged in and profile is complete, go to tracker
      navigate("/tracker");
    } else {
      // User needs to sign up/login
      navigate("/auth");
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
      
      setUser(null);
      setHasCompletedProfile(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };
  
  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Her Season — Your Journey, Your Power"
        description="A warm, science-backed companion for perimenopause and menopause — track symptoms, learn what's happening, and find relief written for real women."
        canonical="https://her-season.lovable.app/"
      />
      {/* Sticky Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/50 animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo/Brand */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex flex-col items-start leading-tight hover:text-primary transition-colors"
            >
              <span className="text-lg font-semibold text-foreground tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Her Season
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground -mt-0.5">
                your journey your power
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Navigation Links - Desktop */}
            <div className="hidden lg:flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/tracker")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                Tracker
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/insights")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                Insights
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/learning")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                Learn
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/sos-toolkit")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                SOS
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/community")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                Community
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/shop")}
                className="font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
              >
                Shop
              </Button>
            </div>

            {/* User Actions */}
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/profile")}
                    className="gap-2 font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
                  >
                    <UserIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Profile</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="gap-2 font-medium text-foreground/80 hover:text-primary hover:bg-transparent"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => navigate("/auth")}
                  className="gap-2"
                >
                  Get Started
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border animate-fade-in">
            <div className="container mx-auto px-4 py-4 space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/tracker");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <Activity className="h-4 w-4" />
                Tracker
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/insights");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <TrendingUp className="h-4 w-4" />
                Insights
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/learning");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <Brain className="h-4 w-4" />
                Learn
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/sos-toolkit");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <Sparkles className="h-4 w-4" />
                SOS
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/community");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <Users className="h-4 w-4" />
                Community
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/shop");
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 font-semibold text-foreground"
              >
                <ShoppingBag className="h-4 w-4" />
                Let's Go Shopping
              </Button>
              
              {!loading && (
                <>
                  {user ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate("/profile");
                          setMobileMenuOpen(false);
                        }}
                        className="w-full justify-start gap-2"
                      >
                        <UserIcon className="h-4 w-4" />
                        Profile
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="w-full justify-start gap-2"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => {
                        navigate("/auth");
                        setMobileMenuOpen(false);
                      }}
                      className="w-full"
                    >
                      Sign In
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[84vh] overflow-hidden pt-14 flex items-end">
        <img
          src="/products/women-hero-group.png"
          alt="Three women laughing together in warm coastal light"
          className="absolute inset-0 h-full w-full object-cover object-center"
          width={1536}
          height={1024}
        />
        <div className="absolute inset-0 bg-background/78" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-14 sm:pb-20 lg:pb-24">
          <div className="max-w-4xl animate-fade-up">
            <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-primary font-semibold mb-5">
              Your journey · your power
            </p>
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-semibold mb-7 text-foreground leading-[0.92]">
              Her Season
            </h1>
            <p className="text-xl md:text-2xl mb-9 text-foreground/75 max-w-2xl leading-relaxed">
              A warm, evidence-based space for tracking symptoms, understanding your body, and feeling less alone through perimenopause and menopause.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="w-full sm:w-auto shadow-lg text-base sm:text-lg px-8 py-6 font-semibold"
                disabled={loading}
              >
                {user && hasCompletedProfile ? "Open your tracker" : "Get started"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={scrollToFeatures}
                className="w-full sm:w-auto border-2 border-primary/30 text-foreground hover:bg-primary/5 hover:border-primary text-base sm:text-lg px-8 py-6 font-semibold"
              >
                Explore Her Season
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Welcome Message - Only for authenticated users with completed profiles */}
      {user && hasCompletedProfile && (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <TrialBanner />
          <RotatingWelcomeMessage />
          <DailyReflection />
        </div>
      )}

      {/* Features — layered editorial stories */}
      <div id="features-section">
        <section className="py-20 bg-[hsl(var(--background))]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">The Her Season Experience</span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 mb-5 text-foreground leading-tight">
              A companion for every part of the journey
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Not a menu of features — a set of quiet, considered places designed to meet you where you are.
            </p>
          </div>
        </section>

        <FeatureStory
          to="/features/tracker"
          index={1}
          kicker="Chapter One — Understand"
          title="Smart Tracking"
          description="Log symptoms, sleep, energy, and lifestyle patterns in seconds. Over time, your data becomes a story — one your doctor can read at a glance."
          bullets={[
            "Doctor-ready reports generated automatically",
            "Pattern detection across symptoms and cycles",
            "Private, encrypted, and yours alone",
          ]}
          image={womanJournalingImage}
          icon={Activity}
          tone="cream"
        />

        <FeatureStory
          to="/sos-toolkit"
          index={2}
          kicker="Chapter Two — Steady"
          title="The SOS Toolkit"
          description="When a hot flush hits mid-meeting, or anxiety arrives at 3am — you have somewhere to go. Guided breathing, grounding, and focus resets in under two minutes."
          bullets={[
            "60-second breathing resets",
            "Grounding exercises for anxiety and overwhelm",
            "Cooling and focus rituals",
          ]}
          image={calmBackgroundImage}
          icon={Sparkles}
          reverse
          tone="sage"
        />

        <FeatureStory
          to="/features/aura"
          index={3}
          kicker="Chapter Three — Illuminate"
          title="Aura AI Insights"
          description="A quiet intelligence that reads between your entries. Aura surfaces patterns you'd never notice alone, and offers gentle, evidence-based next steps."
          bullets={[
            "Personalised pattern analysis",
            "Evidence-based lifestyle suggestions",
            "Conversational, never clinical",
          ]}
          image={aiInsightsImage}
          icon={TrendingUp}
          tone="dusk"
        />

        <FeatureStory
          to="/features/symptom-explorer"
          index={4}
          kicker="Chapter Four — Learn"
          title="Symptom Explorer"
          description="Interactive deep-dives into every symptom — the science, the myths, the tools that actually help. Written like a friend who happens to know the research."
          bullets={[
            "34+ symptoms, plainly explained",
            "Quizzes and self-assessments",
            "Evidence-based interventions",
          ]}
          image={symptomExplorerImage}
          icon={Flame}
          reverse
          tone="blush"
        />

        <FeatureStory
          to="/features/community"
          index={5}
          kicker="Chapter Five — Belong"
          title="Community"
          description="A private, anonymous space where women in the same season share what's working, what isn't, and what nobody else is talking about."
          bullets={[
            "Anonymous by default",
            "Moderated and safe",
            "Real stories, no performance",
          ]}
          image={communitySupportImage}
          icon={Users}
          tone="cream"
        />

        <FeatureStory
          to="/features/goss"
          index={6}
          kicker="Chapter Six — Enjoy"
          title="All the Goss"
          description="Hot takes, expert columns, real stories, and wellness tips. Your daily dose of menopause insight — with the wit and warmth of a favourite magazine."
          bullets={[
            "Weekly columns from clinicians",
            "Real reader stories",
            "Wellness tips you'll actually use",
          ]}
          image={womanReadingNewspaperImage}
          icon={Newspaper}
          reverse
          tone="sage"
        />

        {/* More to explore — compact grid for supporting features */}
        <section className="py-20 bg-[hsl(var(--background))]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Also inside</span>
              <h3 className="font-serif text-3xl md:text-4xl mt-3 text-foreground">More to explore</h3>
            </div>
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FeatureCard
                to="/micro-learning"
                icon={Zap}
                title="Micro-Learning"
                description="Quick 2-3 minute lessons. Learn on the go and unlock badges as you progress."
                image={womanReadingImage}
              />
              <FeatureCard
                to="/learning"
                icon={Brain}
                title="Learning Hub"
                description="Evidence-based content that empowers. Myths debunked, facts explained."
                image={womanLearningImage}
              />
              <FeatureCard
                to="/myth-buster"
                icon={Lightbulb}
                title="Myth Busters"
                description="Swipe through facts and myths. Learn the truth in a fun, engaging way."
                image={mythBustersImage}
              />
              <FeatureCard
                to="/progress"
                icon={Trophy}
                title="Your Progress"
                description="Track your learning journey, earn badges, and celebrate your achievements."
                image={progressMountainImage}
              />
              <FeatureCard
                to="/recipes"
                icon={ChefHat}
                title="Wellness Recipes"
                description="Menopause-friendly meals tailored for symptom relief and vitality."
                image={wellnessRecipesImage}
              />
              <PersonalizedPlanCard />
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section 
        className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url(${happyWomenImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container mx-auto text-center max-w-3xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white animate-fade-in">
            You Are Not Alone on This Journey
          </h2>
          <p className="text-xl text-white/90 mb-8 animate-fade-in leading-relaxed">
            This transition is just the beginning of a powerful new chapter. Join a supportive community 
            where you can find understanding, gain knowledge, and discover tools to help you thrive—not 
            just survive—through perimenopause and beyond.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate("/auth")}
            className="bg-white text-primary hover:bg-white/90 shadow-xl text-lg px-10 py-6 animate-scale-in font-semibold"
          >
            Start Your Free 14-Day Journey
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      <AuraChatButton />
    </div>
  );
};

export default Index;
